/**
 * Linkify — détection et rendu sécurisé des URLs dans du texte brut.
 *
 * Sécurité :
 *   - JAMAIS de dangerouslySetInnerHTML : on construit du JSX par split/map
 *   - rel="noopener noreferrer" sur chaque lien (protège du tabnabbing)
 *   - target="_blank" pour ouvrir hors-page
 *
 * Note WebView (WKWebView dans l'app Apollo Global) :
 *   target="_blank" déclenche `WKWebView decidePolicyForNavigationAction`.
 *   Par défaut, WKWebView ne sait pas ouvrir un nouvel onglet. FlutterFlow
 *   doit configurer le delegate pour rediriger vers le browser externe
 *   (`UIApplication.shared.open(url)`). À tester dans l'app réelle.
 */
import React from "react";

// URL detection : http(s):// + chars URL-safe, sans avaler la ponctuation finale.
const URL_RX = /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]}]/gi;

export function Linkify({ children, className }) {
  const text = typeof children === "string" ? children : String(children ?? "");

  // Collecte des matches via matchAll (sans mutation manuelle).
  const matches = Array.from(text.matchAll(URL_RX));
  if (matches.length === 0) {
    return <span className={className}>{text}</span>;
  }

  // Construction immuable des segments via reduce.
  const segments = matches.reduce(
    (acc, m) => {
      const before = text.slice(acc.cursor, m.index);
      const url = m[0];
      const link = (
        <a
          key={`${m.index}-${url}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800 break-all"
          data-testid="post-link"
        >
          {url}
        </a>
      );
      return {
        cursor: m.index + url.length,
        nodes: before ? [...acc.nodes, before, link] : [...acc.nodes, link],
      };
    },
    { cursor: 0, nodes: [] }
  );

  const tail = text.slice(segments.cursor);
  const final = tail ? [...segments.nodes, tail] : segments.nodes;
  return <span className={className}>{final}</span>;
}
